import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, RevealLines } from "@/components/yanre/Reveal";
import { WHATSAPP_NUMBER, whatsappMessage } from "@/config";
import { track } from "@/lib/track";
import { useLocale } from "@/i18n/LocaleContext";

// PT Studio hero — same cinematic language as the LP hero (approved Dubai asset).
export const PTHero = () => {
  const { t, locale } = useLocale();
  const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMessage({ page: "personal-trainer-studio", lang: locale }))}`;
  const trust = [0, 1, 2, 3].map((i) =>
    t(`pts.hero.trust.${i}`, ["Commercial-Grade", "Direct From Manufacturer", "Factory Warranty", "Logistics Support"][i])
  );

  return (
    <section id="top" className="relative flex min-h-screen items-center overflow-hidden bg-carbon-950" data-testid="pt-hero">
      <div className="absolute inset-0" aria-hidden="true">
        <picture>
          <source
            type="image/webp"
            srcSet="/media/pt-studio-hero-768.webp 768w, /media/pt-studio-hero-1280.webp 1280w, /media/pt-studio-hero-1672.webp 1672w"
            sizes="100vw"
          />
          <img src="/media/pt-studio-hero-1280.jpg" alt="" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-right" />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-t from-carbon-950 via-carbon-950/55 to-carbon-950/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-carbon-950/85 via-carbon-950/35 to-transparent rtl:bg-gradient-to-l" />
      </div>

      <div className="container-edge relative pb-24 pt-32 sm:pt-40">
        <Reveal>
          <div className="mb-8 flex items-center gap-4">
            <span className="h-px w-10 bg-signal" />
            <span className="overline" data-testid="pt-hero-eyebrow">{t("pts.hero.label", "Personal Trainer Studio")}</span>
          </div>
        </Reveal>
        <h1 className="max-w-6xl font-display text-5xl font-extrabold uppercase leading-[0.88] tracking-tight text-white sm:text-7xl lg:text-8xl xl:text-9xl" data-testid="pt-hero-h1">
          <RevealLines
            lines={[
              t("pts.hero.line1", "Your Studio."),
              t("pts.hero.line2", "Your Clients."),
              <span key="biz" className="text-signal">{t("pts.hero.line3", "Your Business.")}</span>,
            ]}
          />
        </h1>
        <Reveal delay={0.15}>
          <p className="mt-8 font-display text-lg font-bold uppercase tracking-wide text-titanium sm:text-xl" data-testid="pt-hero-subhead">
            {t("pts.hero.subhead", "Build your professional training studio with YANRE.")}
          </p>
        </Reveal>
        <Reveal delay={0.22}>
          <p className="mt-4 max-w-xl text-base font-light leading-relaxed text-titanium/85 sm:text-lg" data-testid="pt-hero-copy">
            {t(
              "pts.hero.copy",
              "From your first private studio to a premium personal training facility — commercial-grade equipment, intelligent space planning and direct manufacturer support."
            )}
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <a
              href="#studio-project"
              onClick={() => track("cta_start_project", { placement: "hero_primary", page: "personal-trainer-studio" })}
              className="btn-signal group"
              data-testid="pt-hero-primary-cta"
            >
              {t("pts.hero.ctaPrimary", "Build My Studio")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("whatsapp_click", { placement: "hero_secondary", page: "personal-trainer-studio" })}
              className="btn-ghost border-titanium/30 bg-carbon-950/40 text-titanium backdrop-blur-sm hover:border-signal hover:text-white"
              data-testid="pt-hero-secondary-cta"
            >
              {t("pts.hero.ctaSecondary", "Talk to a Specialist")}
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.4}>
          <ul className="mt-12 flex flex-wrap gap-x-8 gap-y-2" data-testid="pt-hero-trust">
            {trust.map((item) => (
              <li key={item} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/50">
                <span className="h-1 w-1 bg-signal" />
                {item}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
};
